import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Coffee, Sparkles, RefreshCw, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import { recommendationsApi } from "@/lib/crmApi";
import { useSettings } from "@/context/SettingsContext";
import { formatDateTime } from "@/lib/crmHelpers";

/**
 * Minimal markdown renderer (h2, bold, lists). Avoids adding a dependency.
 */
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const blocks = [];
  let listBuf = [];

  const flushList = () => {
    if (listBuf.length) {
      blocks.push({ type: "ul", items: listBuf });
      listBuf = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushList();
      continue;
    }
    if (line.startsWith("## ")) {
      flushList();
      blocks.push({ type: "h2", text: line.slice(3) });
    } else if (line.startsWith("- ")) {
      listBuf.push(line.slice(2));
    } else {
      flushList();
      blocks.push({ type: "p", text: line });
    }
  }
  flushList();

  const renderInline = (text, key) => {
    // Bold **xxx** → <strong>
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
      if (p.startsWith("**") && p.endsWith("**")) {
        return (
          <strong key={`${key}-${i}`} className="font-semibold text-foreground">
            {p.slice(2, -2)}
          </strong>
        );
      }
      return <span key={`${key}-${i}`}>{p}</span>;
    });
  };

  return blocks.map((b, i) => {
    if (b.type === "h2") {
      return (
        <h3
          key={i}
          className="font-heading mt-4 text-sm font-semibold uppercase tracking-wider text-[hsl(var(--accent-gold))] first:mt-0"
        >
          {renderInline(b.text, `h2-${i}`)}
        </h3>
      );
    }
    if (b.type === "ul") {
      return (
        <ul key={i} className="mt-2 space-y-1.5">
          {b.items.map((it, j) => (
            <li
              key={j}
              className="flex gap-2 text-sm leading-relaxed text-foreground"
            >
              <span className="mt-2 h-1 w-1 flex-none rounded-full bg-[hsl(var(--accent-gold))]" />
              <span className="min-w-0 flex-1">{renderInline(it, `li-${i}-${j}`)}</span>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p key={i} className="mt-3 text-sm leading-relaxed text-foreground first:mt-0">
        {renderInline(b.text, `p-${i}`)}
      </p>
    );
  });
}

export default function MorningBriefSection() {
  const { activeProjectId } = useSettings();
  const [brief, setBrief] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [userName, setUserName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadCached = useCallback(async () => {
    setLoading(true);
    try {
      const data = await recommendationsApi.getBrief(activeProjectId);
      setBrief(data.brief || null);
      setGeneratedAt(data.generated_at || null);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [activeProjectId]);

  useEffect(() => {
    loadCached();
  }, [loadCached]);

  // Surface ALL failure modes — the empty-state button used to silently
  // swallow problems (network error before reaching the try/catch boundary,
  // server returning 401, …) which made it look like the button did
  // nothing on click. We log to the console too so a stuck "Génération en
  // cours…" is debuggable from the browser.
  const generate = async (force = false) => {
    setGenerating(true);
    try {
      const data = await recommendationsApi.generateBrief(activeProjectId, force);
      if (!data || (!data.brief && !data.generated_at)) {
        throw new Error("Réponse vide du serveur");
      }
      setBrief(data.brief);
      setGeneratedAt(data.generated_at);
      setUserName(data.user_name || "");
      setProjectName(data.project_name || "");
      if (data.cached) {
        toast.info("Brief du jour déjà généré (cache)");
      } else {
        toast.success("Brief du matin généré ☕");
      }
    } catch (e) {
      // Differentiate the common causes so the toast actually helps the
      // user fix the problem (missing API key vs auth vs network).
      const status = e?.response?.status;
      let detail =
        e?.response?.data?.detail || e?.humanMessage || e?.message || "Erreur inconnue";
      if (status === 401) {
        detail = "Session expirée — reconnectez-vous";
      } else if (status === 502 && /clé api/i.test(String(detail))) {
        detail = "Clé Anthropic absente — ajoutez ANTHROPIC_API_KEY dans backend/.env";
      } else if (status === 502) {
        detail = `Échec IA : ${detail}`;
      }
      console.error("[brief] generate failed", e);
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  // Empty state — invite to generate
  if (!brief && !loading) {
    return (
      <section data-testid="morning-brief-section">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <div className="text-label text-muted-foreground">Coach commercial</div>
            <h2 className="font-heading mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight">
              <Coffee className="h-6 w-6 text-[hsl(var(--accent-gold))]" />
              Brief du matin
            </h2>
          </div>
        </div>
        <div className="rounded-xl border border-dashed border-border/60 bg-gradient-to-br from-[hsl(var(--accent-gold))]/5 via-card to-[hsl(var(--accent-navy))]/5 p-8 text-center">
          <Sparkles className="mx-auto h-8 w-8 text-[hsl(var(--accent-gold))]" />
          <h3 className="font-heading mt-3 text-base font-semibold">
            Génère ton brief commercial personnalisé
          </h3>
          <p className="mx-auto mt-1.5 max-w-md text-xs text-muted-foreground">
            Claude Sonnet 4.5 analyse tes KPIs, recommandations et rappels du jour
            pour te livrer un mémo motivant et actionnable.
          </p>
          <Button
            onClick={() => generate(false)}
            disabled={generating}
            data-testid="brief-generate-btn"
            className="mt-4 gap-2 bg-[hsl(var(--accent-navy))] text-white shadow-md hover:bg-[hsl(var(--accent-navy))]/90"
          >
            <Sun className={`h-4 w-4 ${generating ? "animate-spin" : ""}`} />
            {generating ? "Génération en cours..." : "Générer mon brief"}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="morning-brief-section">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-label text-muted-foreground">
            Coach commercial · alimenté par Claude Sonnet 4.5
          </div>
          <h2 className="font-heading mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Coffee className="h-6 w-6 text-[hsl(var(--accent-gold))]" />
            Brief du matin
          </h2>
          {generatedAt && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Généré le {formatDateTime(generatedAt)}
              {projectName && ` · ${projectName}`}
              {userName && ` · pour ${userName}`}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => generate(true)}
          disabled={generating}
          data-testid="brief-regenerate-btn"
          className="gap-1.5 text-xs"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${generating ? "animate-spin" : ""}`} />
          Régénérer
        </Button>
      </div>

      <div
        className="relative overflow-hidden rounded-xl border border-[hsl(var(--accent-gold))]/40 bg-gradient-to-br from-[hsl(var(--accent-gold))]/5 via-card to-[hsl(var(--accent-navy))]/5 p-6 shadow-sm"
        data-testid="brief-content"
      >
        <div className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-[hsl(var(--accent-gold))]/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-12 -bottom-12 h-48 w-48 rounded-full bg-[hsl(var(--accent-navy))]/10 blur-3xl" />
        <div className="relative space-y-1">
          {brief ? renderMarkdown(brief) : (
            <p className="text-sm text-muted-foreground">Chargement...</p>
          )}
        </div>
      </div>
    </section>
  );
}
